import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    CircularProgress,
    Dialog,
    TablePagination,
    IconButton,
    Tooltip,
    Stack,
    Chip
} from '@mui/material';
import { FetchOrganizations, DeleteOrganization } from '../../../../services/api';
import CreateOrganizationForm from './CreateOrganizationForm';
import usePermission from '../../../../hooks/usePermission';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Grid, Card, CardContent, CardActionArea } from '@mui/material';

const OrganizationsList = () => {
    // Data State
    const [orgTree, setOrgTree] = useState([]); // Full hierarchical structure from backend
    const [orgs, setOrgs] = useState([]);      // Currently displayed organizations (subset of tree)

    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editData, setEditData] = useState(null);

    // Pagination (Local)
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    // Drilldown State
    const [selectedParentId, setSelectedParentId] = useState(null);
    const [selectedParentName, setSelectedParentName] = useState("");

    // Delete confirmation dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const token = localStorage.getItem('accessToken');
    const { can, user } = usePermission();
    const isGlobalUser = user && (user.organizationId === null || user.organizationId === undefined);

    // Permissions
    const canCreate = can('organization.create');
    const canUpdate = can('organization.update');
    const canDelete = can('organization.delete');

    useEffect(() => {
        loadOrgs();
    }, []);

    // Local Logic: Update displayed orgs when tree or selection changes
    useEffect(() => {
        if (!orgTree || orgTree.length === 0) {
            setOrgs([]);
            setTotalCount(0);
            return;
        }

        if (isGlobalUser) {
            if (selectedParentId) {
                const parent = orgTree.find(p => (p.id || p._id) === selectedParentId);
                const children = parent?.children || [];
                setOrgs(children);
                setTotalCount(children.length);
            } else {
                setOrgs(orgTree);
                setTotalCount(orgTree.length);
            }
        } else {
            // Scoped user: backend returns tree [ { ...userOrg, children: [...] } ]
            const rootOrg = orgTree[0];
            if (rootOrg) {
                const children = rootOrg.children || [];
                setOrgs(children);
                setTotalCount(children.length);
            }
        }
    }, [orgTree, selectedParentId, isGlobalUser]);

    const loadOrgs = async () => {
        setLoading(true);
        try {
            const response = await FetchOrganizations(token, { limit: 100 });
            const data = response?.data?.data || response?.data || [];
            if (Array.isArray(data)) {
                setOrgTree(data);
            }
        } catch (error) {
            console.error("Failed to fetch organizations", error);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCreate = () => {
        setEditData(null);
        setOpenDialog(true);
    };

    const handleEdit = (org) => {
        const isParentOrg = !org.parentId;
        const isSuperAdmin = can('organization.parent.create');

        if (isParentOrg && !isSuperAdmin) {
            alert("You do not have permission to edit Parent Organizations.");
            return;
        }

        setEditData(org);
        setOpenDialog(true);
    };

    const handleDeleteClick = (org) => {
        setDeletingId(org.id || org._id);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!deletingId) return;
        try {
            const response = await DeleteOrganization(deletingId, token);
            if (response?.status === 200 || response?.status === 204) {
                loadOrgs();
                setDeleteDialogOpen(false);
                setDeletingId(null);
            } else {
                alert("Failed to delete organization.");
            }
        } catch (error) {
            console.error("Delete failed", error);
        }
    };

    const cancelDelete = () => {
        setDeleteDialogOpen(false);
        setDeletingId(null);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditData(null);
    };

    const handleSuccess = () => {
        loadOrgs();
    };

    const handleSelectParent = (parent) => {
        setSelectedParentId(parent.id || parent._id);
        setSelectedParentName(parent.organizationName);
        setPage(0);
    };

    const handleGoBack = () => {
        setSelectedParentId(null);
        setSelectedParentName("");
        setPage(0);
    };

    // Calculate Paginated Data
    const paginatedOrgs = orgs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Identify parent org for scoped users
    const parentOrgForScoped = !isGlobalUser ? orgTree[0] : null;

    return (
        <Box p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Stack direction="row" spacing={2} alignItems="center">
                    {selectedParentId && (
                        <IconButton onClick={handleGoBack} color="primary">
                            <ArrowBackIcon />
                        </IconButton>
                    )}
                    <Box>
                        <Typography variant="h5" fontWeight="bold">
                            {selectedParentId ? `Organizations: ${selectedParentName}` : "Organization Management"}
                        </Typography>
                        {selectedParentId && (
                            <Typography variant="body2" color="textSecondary">
                                Viewing child organizations
                            </Typography>
                        )}
                    </Box>
                </Stack>

                {canCreate && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleCreate}
                    >
                        Create Organization
                    </Button>
                )}
            </Box>

            {isGlobalUser && !selectedParentId ? (
                <Box>
                    <Typography variant="h6" mb={2} color="textSecondary">Parent Organizations</Typography>
                    {loading ? (
                        <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
                    ) : (
                        <Grid container spacing={3}>
                            {orgs.map((org) => (
                                <Grid item xs={12} sm={6} md={4} key={org.id || org._id}>
                                    <Card
                                        elevation={3}
                                        sx={{
                                            borderRadius: 2,
                                            transition: 'transform 0.2s',
                                            '&:hover': { transform: 'scale(1.02)' }
                                        }}
                                    >
                                        <CardActionArea onClick={() => handleSelectParent(org)} sx={{ p: 2 }}>
                                            <Box display="flex" alignItems="center" mb={2}>
                                                <BusinessIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                                                <Box>
                                                    <Typography variant="h6" noWrap>{org.organizationName}</Typography>
                                                    <Typography variant="caption" color="textSecondary">{org.organizationCode}</Typography>
                                                </Box>
                                            </Box>
                                            <Stack direction="row" spacing={1} mb={2}>
                                                <Chip label={org.organizationType} size="small" variant="outlined" />
                                                <Chip
                                                    label={org.isActive ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    color={org.isActive ? 'success' : 'default'}
                                                />
                                            </Stack>
                                            <Box display="flex" justifyContent="flex-end">
                                                {canUpdate && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); handleEdit(org); }}
                                                        sx={{ mr: 1 }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                                <Typography variant="button" color="primary">Explore &rarr;</Typography>
                                            </Box>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            ))}
                            {orgs.length === 0 && (
                                <Grid item xs={12}>
                                    <Typography align="center" color="textSecondary" py={5}>No parent organizations found</Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </Box>
            ) : (
                <Box>
                    {/* Parent Org Box for Scoped Users */}
                    {parentOrgForScoped && (
                        <Box mb={3}>
                            <Typography variant="h6" mb={2} color="textSecondary">Parent Organization</Typography>
                            <Grid container>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Tooltip title="This is your parent organization. You can manage child organizations in the table below.">
                                        <Card
                                            elevation={3}
                                            sx={{
                                                borderRadius: 2,
                                                border: '2px solid',
                                                borderColor: 'primary.main',
                                                bgcolor: 'primary.50',
                                                cursor: 'default'
                                            }}
                                        >
                                            <CardActionArea sx={{ p: 2, '&:hover': { bgcolor: 'primary.50' } }}>
                                                <Box display="flex" alignItems="center" mb={1}>
                                                    <BusinessIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                                                    <Box>
                                                        <Typography variant="h6" noWrap>
                                                            {parentOrgForScoped.organizationName}
                                                            {" (Parent)"}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {parentOrgForScoped.organizationCode}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Stack direction="row" spacing={1}>
                                                    <Chip label="DE parent organization" size="small" color="primary" variant="filled" />
                                                    <Chip
                                                        label={parentOrgForScoped.isActive ? 'Active' : 'Inactive'}
                                                        size="small"
                                                        color={parentOrgForScoped.isActive ? 'success' : 'default'}
                                                    />
                                                </Stack>
                                            </CardActionArea>
                                        </Card>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    <Paper elevation={2}>
                        <TableContainer>
                            <Table>
                                <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableRow>
                                        <TableCell><strong>Name</strong></TableCell>
                                        <TableCell><strong>Code</strong></TableCell>
                                        <TableCell><strong>Type</strong></TableCell>
                                        <TableCell><strong>Parent Org</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell align="center"><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 3 }}><CircularProgress /></TableCell>
                                        </TableRow>
                                    ) : paginatedOrgs.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                                <Typography color="textSecondary">No organizations found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        paginatedOrgs.map((org) => (
                                            <TableRow key={org.id || org._id} hover>
                                                <TableCell>{org.organizationName}</TableCell>
                                                <TableCell>{org.organizationCode}</TableCell>
                                                <TableCell><Chip label={org.organizationType} size="small" variant="outlined" /></TableCell>
                                                <TableCell>{org.parentId ? (org.parentOrganization?.organizationName || 'Child') : 'Parent (Root)'}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={org.isActive ? 'Active' : 'Inactive'}
                                                        color={org.isActive ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Stack direction="row" spacing={1} justifyContent="center">
                                                        {canUpdate && (
                                                            <Tooltip title="Edit">
                                                                <IconButton
                                                                    size="small"
                                                                    color="primary"
                                                                    onClick={() => handleEdit(org)}
                                                                    disabled={!org.parentId && !can('organization.parent.create')}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        {canDelete && (
                                                            <Tooltip title="Delete">
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => handleDeleteClick(org)}
                                                                    disabled={!org.parentId && !can('organization.parent.create')}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={totalCount}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Paper>
                </Box>
            )}

            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <CreateOrganizationForm
                    onClose={handleCloseDialog}
                    onSuccess={handleSuccess}
                    initialData={editData}
                    initialParentId={selectedParentId}
                />
            </Dialog>
            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={cancelDelete}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography id="delete-dialog-title" variant="h6" fontWeight="bold" gutterBottom>
                        Delete Organization
                    </Typography>
                    <Typography id="delete-dialog-description" variant="body1" sx={{ mb: 3 }}>
                        Are you sure you want to delete this organization? This action cannot be undone.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button onClick={cancelDelete} variant="outlined" color="primary">
                            Cancel
                        </Button>
                        <Button onClick={confirmDelete} variant="contained" color="error" autoFocus>
                            Delete
                        </Button>
                    </Stack>
                </Box>
            </Dialog>
        </Box>
    );
};

export default OrganizationsList;
